<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import { ScheduleCoachingFormInterval } from '@/js/stores/scheduleCoaching';
    import DatePickerInput from '../../DatePickerInput.vue';
    import { Frequency } from 'rrule';
    import SelectInput from '../../SelectInput.vue';
    import dayjs from 'dayjs';
    import { computed, watch } from 'vue';
    import ToggleDayInput from './ToggleDayInput.vue';
    import FormGroup from '../../FormGroup.vue';
    import InputLabel from '../../InputLabel.vue';
    import { TriangleAlertIcon } from '@lucide/vue';

    const interval = defineModel<ScheduleCoachingFormInterval>('interval', { required: true });

    defineProps<{
        index: number;
    }>();

    const FREQUENCY_OPTIONS = [
        { label: __('frequency-daily'), value: Frequency.DAILY },
        { label: __('frequency-weekly'), value: Frequency.WEEKLY },
        { label: __('frequency-monthly'), value: Frequency.MONTHLY },
    ];

    const INTERVAL_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

    const invalidEndDateError = computed(() => {
        if (!interval.value.endDate) return false;
        if (dayjs(interval.value.startDate).isAfter(dayjs(interval.value.endDate))) {
            return true;
        }
        return false;
    });

    const monthlyLastDayInfo = computed(() => {
        if (interval.value.frequency !== Frequency.MONTHLY) return null;
        const start = dayjs(interval.value.startDate);
        if (!start.isValid()) return null;
        const day = start.date();
        if (day < 29) return null;
        return day;
    });

    watch(
        () => interval.value.frequency,
        (frequency) => {
            if (frequency !== Frequency.WEEKLY) {
                interval.value.byweekday = {
                    MO: false,
                    TU: false,
                    WE: false,
                    TH: false,
                    FR: false,
                    SA: false,
                    SU: false,
                };
            }
        }
    );
</script>
<template>
    <div class="interval-box-border">
        <FormGroup :help-hint="__('show-info')">
            <template #header>
                <div class="normal-case">
                    <div class="heading-md text-primary-beet">
                        {{ __('schedule-coaching-interval-title') }}
                    </div>
                </div>
            </template>
            <template #help>
                {{ __('schedule-coaching-interval-help') }}
            </template>

            <div class="mt-3">
                <InputLabel :for="'start-date-' + index">{{ __('start-date') }}</InputLabel>
                <div class="flex-1 relative -mb-5">
                    <DatePickerInput
                        :id="'start-date-' + index"
                        v-model="interval.startDate"
                        format="YYYY-MM-DD"
                        class="w-full"
                    />
                </div>
            </div>
            <div>
                <InputLabel :for="'interval' + index">
                    {{ __('schedule-coaching-form-repeat-every') }}
                </InputLabel>
                <div class="flex flex-1 gap-2">
                    <SelectInput :id="'interval' + index" v-model="interval.interval" size="small">
                        <option
                            v-for="intervalOption in INTERVAL_OPTIONS"
                            :key="intervalOption"
                            :value="intervalOption"
                        >
                            {{ intervalOption }}
                        </option>
                    </SelectInput>
                    <SelectInput
                        :id="'FREQUENCY' + index"
                        v-model="interval.frequency"
                        :aria-label="__('schedule-coaching-frequency-select')"
                        size="full"
                    >
                        <option
                            v-for="frequency in FREQUENCY_OPTIONS"
                            :key="frequency.value"
                            :value="frequency.value"
                        >
                            {{ frequency.label }}
                        </option>
                    </SelectInput>
                </div>
                <p
                    v-if="monthlyLastDayInfo"
                    class="text-caption text-gray-dark mt-2 flex items-center"
                >
                    <TriangleAlertIcon
                        class="size-4 inline-block -mt-px mr-1.5 self-start stroke-sentiment-negative"
                    />
                    {{
                        __('schedule-coaching-form-monthly-last-day-info', {
                            day: String(monthlyLastDayInfo),
                        })
                    }}
                </p>
            </div>
            <div
                v-if="interval.frequency === Frequency.WEEKLY"
                class="border-gray-lightest border-1 rounded-md p-2.5"
            >
                <div class="flex gap-4 mb-4">
                    <ToggleDayInput
                        id="WEEKDAYS.MO"
                        v-model="interval.byweekday.MO"
                        :label="__('monday-short')"
                    />
                    <ToggleDayInput
                        id="WEEKDAYS.TU"
                        v-model="interval.byweekday.TU"
                        :label="__('tuesday-short')"
                    />
                    <ToggleDayInput
                        id="WEEKDAYS.WE"
                        v-model="interval.byweekday.WE"
                        :label="__('wednesday-short')"
                    />
                    <ToggleDayInput
                        id="WEEKDAYS.TH"
                        v-model="interval.byweekday.TH"
                        :label="__('thursday-short')"
                    />
                    <ToggleDayInput
                        id="WEEKDAYS.FR"
                        v-model="interval.byweekday.FR"
                        :label="__('friday-short')"
                    />
                    <ToggleDayInput
                        id="WEEKDAYS.SA"
                        v-model="interval.byweekday.SA"
                        :label="__('saturday-short')"
                    />
                    <ToggleDayInput
                        id="WEEKDAYS.SU"
                        v-model="interval.byweekday.SU"
                        :label="__('sunday-short')"
                    />
                </div>
                <p class="text-caption">
                    {{ __('schedule-coaching-form-weekday-tip') }}
                </p>
            </div>
            <div>
                <InputLabel :for="'end-date-' + index">{{ __('end-date') }}</InputLabel>

                <div class="-mb-5">
                    <DatePickerInput
                        :id="'end-date-' + index"
                        v-model="interval.endDate"
                        format="YYYY-MM-DD"
                        class="w-full"
                    />
                </div>
                <p v-if="invalidEndDateError" class="text-caption text-error">
                    {{ __('schedule-coaching-form-end-date-error') }}
                </p>
            </div>
        </FormGroup>
    </div>
</template>
<style scoped>
    .interval-box-border:nth-last-of-type(n + 2) {
        border-bottom: 1px solid var(--color-gray-lightest);
        margin-bottom: 20px;
    }
</style>
